import merge from "lodash/merge";
import { createTheme, responsiveFontSizes } from "@mui/material";
import { THEMES } from "./constants";
import { lightShadows, darkShadows } from "./shadows";

const baseOptions = {
  direction: "ltr",
  components: {
    MuiTextField: {
      styleOverrides: {
        root: {
          '& input[type=color]': {
            cursor: 'pointer',
          }
        }
      }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: "10px",
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        fallback: {
          height: "75%",
          width: "75%",
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        "*": {
          boxSizing: "border-box",
        },
        html: {
          MozOsxFontSmoothing: "grayscale",
          WebkitFontSmoothing: "antialiased",
          height: "100%",
          width: "100%",
          // Guards the true document root, not just DefaultLayout's <Main>
          // (which already clips overflow-x for its own content). react-
          // beautiful-dnd moves the actively-dragged card via a CSS
          // transform/`position: fixed`-style lift that escapes Main's
          // clipping entirely (fixed-position content is clipped relative to
          // the viewport, not an ancestor's overflow box, unless that
          // ancestor has its own transform — Main has none). Dragging a card
          // far enough in either direction was therefore inflating
          // `document.documentElement`'s scrollable width with nothing left
          // to stop it, which is what actually produced the page-wide
          // horizontal scrollbar. Every page that genuinely needs horizontal
          // scroll already wraps that content in its own overflow-x:auto
          // container (see Main's comment) — nothing relies on html/body
          // scrolling sideways, so this is safe everywhere.
          overflowX: "hidden",
        },
        body: {
          height: "100%",
          overflowX: "hidden",
        },
        "input, textarea, select, .MuiInputBase-input, .MuiAutocomplete-input, .MuiSelect-select, .MuiNativeSelect-select":
        {
          "@supports (-webkit-touch-callout: none)": {
            // !important so no component-level sx (present or future) can
            // reintroduce iOS Safari's auto-zoom-on-focus, which fires
            // whenever a focused form field's computed font-size is under
            // 16px. `-webkit-touch-callout` only exists on iOS WebKit, so
            // this never touches desktop/Android font sizing.
            fontSize: "16px !important",
          },
        },
        "#root": {
          height: "100%",
        },
        "#nprogress .bar": {
          zIndex: "2000 !important",
        },
      },
    },
    MuiCardHeader: {
      defaultProps: {
        titleTypographyProps: {
          variant: "h6",
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 3,
          overflow: "hidden",
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: "auto",
          marginRight: "16px",
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: "none",
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        container: {
          '@media (max-width: 900px)': {
            alignItems: 'flex-start',
          },
        },
        paperFullScreen: {
          width: '100%',
          height: '100%',
          maxHeight: '100%',
          maxWidth: '100%',
          margin: 0,
          borderRadius: 0,
          paddingTop: 'env(safe-area-inset-top, 0px)',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          cursor: "pointer",
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        listbox: ({ theme }) => {
          const thumb = theme.palette.scrollbarThumb;
          const track = theme.palette.scrollbarBackground;
          const thumbHover =
            theme.palette.mode === "dark"
              ? "rgba(255, 255, 255, 0.45)"
              : "rgba(0, 0, 0, 0.35)";
          return {
            scrollbarWidth: "thin",
            scrollbarColor: `${thumb} ${track}`,
            "&::-webkit-scrollbar": {
              width: 8,
              height: 8,
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: track,
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: thumb,
              borderRadius: 4,
              border: `1px solid ${track}`,
            },
            "&::-webkit-scrollbar-thumb:hover": {
              backgroundColor: thumbHover,
            },
          };
        },
      },
    },
  },
  typography: {
    button: {
      fontWeight: 600,
    },
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji"',
    h1: {
      fontWeight: 600,
      fontSize: "3.5rem",
    },
    h2: {
      fontWeight: 600,
      fontSize: "3rem",
    },
    h3: {
      fontWeight: 600,
      fontSize: "2.25rem",
    },
    h4: {
      fontWeight: 600,
      fontSize: "2rem",
    },
    h5: {
      fontWeight: 600,
      fontSize: "1.5rem",
    },
    h6: {
      fontWeight: 600,
      fontSize: "1.125rem",
    },
    overline: {
      fontWeight: 600,
    },
  },
};

/** Role badges on Users › Roles › Hierarchy (red is the admin role's, the rest rotate). */
const lightRoleTints = [
  { background: '#FEF2F2', border: '#FECACA', icon: '#B91C1C' },
  { background: '#F5F3FF', border: '#DDD6FE', icon: '#6D28D9' },
  { background: '#EFF6FF', border: '#BFDBFE', icon: '#1D4ED8' },
  { background: '#ECFDF5', border: '#A7F3D0', icon: '#047857' },
  { background: '#FFFBEB', border: '#FDE68A', icon: '#B45309' },
  { background: '#FDF2F8', border: '#FBCFE8', icon: '#BE185D' },
  { background: '#F0F9FF', border: '#BAE6FD', icon: '#0369A1' },
];
const darkRoleTints = [
  { background: 'rgba(239, 68, 68, 0.15)', border: 'rgba(239, 68, 68, 0.3)', icon: '#F87171' },
  { background: 'rgba(139, 92, 246, 0.15)', border: 'rgba(139, 92, 246, 0.3)', icon: '#A78BFA' },
  { background: 'rgba(59, 130, 246, 0.15)', border: 'rgba(59, 130, 246, 0.3)', icon: '#60A5FA' },
  { background: 'rgba(16, 185, 129, 0.15)', border: 'rgba(16, 185, 129, 0.3)', icon: '#34D399' },
  { background: 'rgba(245, 158, 11, 0.15)', border: 'rgba(245, 158, 11, 0.3)', icon: '#FBBF24' },
  { background: 'rgba(236, 72, 153, 0.15)', border: 'rgba(236, 72, 153, 0.3)', icon: '#F472B6' },
  { background: 'rgba(14, 165, 233, 0.15)', border: 'rgba(14, 165, 233, 0.3)', icon: '#38BDF8' },
];
/** Users › Departments / Staff Schedules: the colour of a shift type or department (backend `staffColors` keys). */
const lightStaffTints = {
  purple: { background: '#F5F3FF', border: '#DDD6FE', text: '#6D28D9' },
  blue: { background: '#EFF6FF', border: '#BFDBFE', text: '#1D4ED8' },
  green: { background: '#ECFDF5', border: '#A7F3D0', text: '#047857' },
  yellow: { background: '#FEFCE8', border: '#FDE68A', text: '#A16207' },
  orange: { background: '#FFF7ED', border: '#FED7AA', text: '#C2410C' },
  red: { background: '#FEF2F2', border: '#FECACA', text: '#B91C1C' },
  pink: { background: '#FDF2F8', border: '#FBCFE8', text: '#BE185D' },
  teal: { background: '#F0FDFA', border: '#99F6E4', text: '#0F766E' },
  indigo: { background: '#EEF2FF', border: '#C7D2FE', text: '#4338CA' },
  gray: { background: '#F9FAFB', border: '#E5E7EB', text: '#374151' },
};
const darkStaffTints = {
  purple: { background: 'rgba(139, 92, 246, 0.15)', border: 'rgba(139, 92, 246, 0.3)', text: '#A78BFA' },
  blue: { background: 'rgba(59, 130, 246, 0.15)', border: 'rgba(59, 130, 246, 0.3)', text: '#60A5FA' },
  green: { background: 'rgba(16, 185, 129, 0.15)', border: 'rgba(16, 185, 129, 0.3)', text: '#34D399' },
  yellow: { background: 'rgba(234, 179, 8, 0.15)', border: 'rgba(234, 179, 8, 0.3)', text: '#FACC15' },
  orange: { background: 'rgba(249, 115, 22, 0.15)', border: 'rgba(249, 115, 22, 0.3)', text: '#FB923C' },
  red: { background: 'rgba(239, 68, 68, 0.15)', border: 'rgba(239, 68, 68, 0.3)', text: '#F87171' },
  pink: { background: 'rgba(236, 72, 153, 0.15)', border: 'rgba(236, 72, 153, 0.3)', text: '#F472B6' },
  teal: { background: 'rgba(20, 184, 166, 0.15)', border: 'rgba(20, 184, 166, 0.3)', text: '#2DD4BF' },
  indigo: { background: 'rgba(99, 102, 241, 0.15)', border: 'rgba(99, 102, 241, 0.3)', text: '#818CF8' },
  gray: { background: 'rgba(156, 163, 175, 0.15)', border: 'rgba(156, 163, 175, 0.3)', text: '#D1D5DB' },
};

const themesOptions = {
  [THEMES.LIGHT]: {
    components: {
      MuiInputBase: {
        styleOverrides: {
          input: {
            "&::placeholder": {
              opacity: 0.86,
              color: "#42526e",
            },
          },
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            fontWeight: "bold",
          },
        },
      },
    },
    palette: {
      topNavSvg: '#5b5b5b',
      iosMoveSVG: '#5b5b5b',
      basicBlackWhite: 'black',
      basicWhiteBlack: 'white',
      chartColors: [
        '#FF6E6E',
        '#938AE1',
        '#76C96D',
        '#6BA7F5',
        '#FE9E21',
        '#D567B4',
        '#0DAA94',
        '#CACE4C',
        '#B96161',
        '#D1AC77',
        '#7FCEE2',
        '#B986EC',
        '#B81955',
        '#B9D653',
        '#CF30AF'
      ],
      dashboardWarning: {
        high: {
          main: '#D22626',
          contrastText: "#ffffff",
          svgColor: "#ee404c"
        },
        medium: {
          main: '#e3750a',
          contrastText: "#ffffff",
          svgColor: "#ffa30b"
        },
        low: {
          main: '#e3c500',
          contrastText: "#ffffff",
          svgColor: "#ffee00"
        },
        info: {
          main: '#569965',
          contrastText: '#ffffff',
          svgColor: '#ffffff'
        }
      },
      card: {
        background: '#f4f5f7'
      },
      chart: {
        even: "#7648a3",
        odd: "#b493be",
      },
      white: "#fff",
      darkGrey: "#5B5B5B",
      border: "#BDBDBD",
      disabled: "#d6d7d9",
      overlay: "rgba(0, 0, 0, 0.50)",
      editButtonBackground: "rgba(0, 0, 0, 0.50)",
      imageEditButtonBackground: "rgba(255, 255, 255, 0.9)",
      imageEditButtonHoverBackground: "rgba(255, 255, 255, 1)",
      boxShadow: "#6b778c",
      box: "#663e83",
      secondaryTitle: "#6b778c",
      svg: "#5b5b5b",
      // delete: "#d95c5c",
      delete: "#BD3737",
      autoMessages: {
        promoBoxBackground: 'rgba(235, 66, 66, 0.2)',
        promoCode: '#EB4242 '
      },
      sendType: {
        sms: '#E3C500',
        email: '#1CB1DE',
        notification: '#E3750A',
        whatsapp: '#83BD37'
      },
      selected: {
        chip: "#86469a",
      },
      autocompleteChip: "#E2E2E2",
      previewBackground: "#F2F2F2",
      cardShadow: "#E2E2E2",
      buttonShadow: "rgba(0, 0, 0, 0.3)",
      skeleton: {
        light: "#dcdee0",
        dark: "#c8cdd3",
      },
      descriptionItemBackground: "#eaebed",
      iconPreviewBackground: "#dfecd0",
      iconOverlay: "rgba(255, 255, 255, 0.2)",
      iconButtonShadow: "grey",
      unselectedTabBackground: "#f4f5f7",
      dateIconBackground: "#ddcfe2",
      editingOverlay: "rgba(200, 200, 200, 0.5)",
      scrollbarBackground: "rgba(0, 0, 0, 0.05)",
      scrollbarThumb: "rgba(0, 0, 0, 0.2)",
      phoneInput: {
        dropdownBoxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
        scrollbarTrack: 'rgba(0, 0, 0, 0.05)',
        scrollbarThumb: 'rgba(0, 0, 0, 0.2)',
        scrollbarThumbHover: 'rgba(0, 0, 0, 0.3)',
      },
      avatar: {
        fallbackBackground: "#d8d8d8",
      },
      shadow: {
        input: "0px 1px 2px rgba(0, 0, 0, 0.12),0px 0px 0px 1px rgba(0, 0, 0, 0.05)",
      },
      cancelButton: {
        main: "#757575",
        hover: "#616161",
      },
      copyButton: {
        default: {
          background: "#5b5b5b",
          color: "#ffffff",
          hover: "#4a4a4a",
        },
        copied: {
          background: "#4caf50",
          color: "#ffffff",
          hover: "#43a047",
        },
      },
      reportTabs: '#EEEEEE',
      printerLocationButton: {
        unselected: {
          border: '#5b5b5b',
          text: '#5b5b5b',
          hoverBackground: 'rgba(0,0,0,0.04)',
        },
      },
      segmentedControl: {
        container: '#e0e0e0',
        activeBackground: '#ffffff',
        activeText: '#212B36',
        inactiveText: '#637381',
        hoverBackground: 'rgba(0, 0, 0, 0.04)',
      },
      pricingLevels: {
        inputBorder: '#BDBDBD',
        inputBackground: '#ffffff',
        inputUnselectedBackground: 'rgba(0, 0, 0, 0.02)',
        cardShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
        cardHoverShadow: '0 4px 16px rgba(0, 0, 0, 0.12)',
        cardDisabledShadow: '0 1px 4px rgba(0, 0, 0, 0.04)',
        chipBackground: '#f4e1fe',
        searchBarBackground: '#f5f5f5',
      },
      switch: {
        thumb: {
          light: "#ffffff",
          dark: "#bdbdbd",
          color: "#ffffff",
        },
        track: {
          light: "#b7b7b7",
          dark: "#616161",
        },
        focusVisible: {
          light: "#33cf4d",
          dark: "#4caf50",
        },
        focusBorder: {
          light: "#ffffff",
          dark: "#222b36",
        },
      },
      orderStatus: {
        isPaid: "#1FB719",
        notPaid: "#EB4242",
        canceledBackground: "rgba(255, 87, 0, 0.28)",
        delayedBackground: "rgba(255, 0, 0, 0.18)",
        rejectedBackground: "rgba(192, 192, 192, 0.5)",
      },
      orderType: {
        dineIn: { main: "#9463A4", selected: '#f4e1fe', transparent: 'rgba(169, 101, 230, 0.08)' },
        delivery: { main: '#4077D0', selected: 'rgba(169, 201, 252, 0.6)', transparent: '#4077D030' },
        takeOut: { main: '#f97316', selected: 'rgba(252, 198, 144, 0.5)', transparent: '#f973161A' },
      },
      userMenu: {
        background: "#f4f5f7",
        icons: "#8130cb",
      },
      messages: {
        background: "#f8f8f8",
        headerBackground: "#f3f3f3",
      },
      pageTitle: "#5b5b5b",
      rateStar: "#97c24c",
      navItems: {
        active: {
          subText: "#723087",
          text: "#000",
          arrow: "black",
          background: "rgba(0, 0, 0, 0.04)"
        },
        default: {
          text: "#212B36",
          background: "transparent",
          hover: "rgba(0, 0, 0, 0.04)"
        },
        arrow: "black",
      },
      menuButton: {
        activeBackgroundColor: "#dadada",
        backgroundColor: "#f4f5f7",
      },
      disabledSearch: {
        background: "#e8e8e8",
      },
      titleBox: {
        contrastText: "#ffffff",
        backgroundColor: "#723087",
      },
      action: {
        active: "#6b778c",
      },
      background: {
        default: "#f4f5f7",
        paper: "#ffffff",
        hover: "#e5dee7",
        selected: "#eee3f6",
        chipSelected: "#c0a9c6",
        tableDropdown: "#c5c5c5",
        tableEven: "#eaebed",
        tableOdd: "#f4f5f7",
        shape: '#B493BE72',
      },
      activity: {
        isActive: "#7648a3",
        isInactive: "#D1D1D1",
        isAway: "#FFBD08",
      },
      error: {
        contrastText: "#ffffff",
        main: "#ff0000",
        light: '#ffcccc'
      },
      secondary: {
        contrastText: "#ffffff",
        main: "#5b5b5b",
        light: '#9D9D9D'
      },
      whiteButton: {
        contrastText: "#5b5b5b",
        main: "#f5f5f5",
      },
      primaryContrast: {
        contrastText: "#172b4d",
        main: "#ffffff",
      },
      mode: "light",
      primary: {
        contrastText: "#ffffff",
        main: "#723087",
        light: '#f4e1fe',
      },
      secondaryTable: {
        contrastText: "#ffffff",
        main: "#cecece",
        mainLight: '#b493be'
      },
      success: {
        contrastText: "#ffffff",
        main: "#4caf50",
        light: '#e8f5e9',
        dark: '#2e7d32',
      },
      text: {
        primary: "#172b4d",
        secondary: "#6b778c",
        third: '#000'
      },
      warning: {
        contrastText: "#ffffff",
        main: "#ff9800",
        light: '#fff3e0',
        dark: '#e65100',
      },
      sidebarSelectedBackground: '#F5F5F4',
      sidebar: {
        activeText: '#212B36',
        inactiveText: '#666666',
        secondaryText: '#637381',
        scrollbar: '#cccccc',
        divider: 'rgba(102, 102, 102, 0.2)',
        boxShadow: 'inset 0 0 5px rgba(0, 0, 0, 0.35)',
        dropdownShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
        icon: '#727272',
        logout: "#d55f5a",
        text: "#757575",
        // Hovered rows (the rail's hover pill is the primary colour).
        hoverBg: 'rgba(0, 0, 0, 0.04)',
        shadow: '2px 0 8px 0 rgba(0, 0, 0, 0.10)',
      },
      dashboardTabs: {
        backgroundColor: '#e0e0e0',
        textColor: '#212B36',
        hoverBackgroundColor: '#d5d5d5',
      },
      dashboard: {
        linearGradient: {
          start: "#9463A4",
          end: "#d6cbda"
        }
      },
      link: {
        main: '#0d47a1',
        hover: '#1565c0',
      },
      salesCards: {
        revenue: {
          background: '#F0FDF4',
          border: '#BBF7D0',
          icon: '#10B981',
          text: '#10B981',
        },
        orders: {
          background: '#EFF6FF',
          border: '#BFDBFE',
          icon: '#3B82F6',
          text: '#3B82F6',
        },
        customers: {
          background: '#FAF5FF',
          border: '#E9D5FF',
          icon: '#8B5CF6',
          text: '#8B5CF6',
        },
        avgOrder: {
          background: '#FFF7ED',
          border: '#FED7AA',
          icon: '#F59E0B',
          text: '#F59E0B',
        },
        discount: {
          background: '#FEE2E2',
          border: '#FECACA',
          icon: '#EF4444',
          text: '#EF4444',
        },
      },
      roleTints: lightRoleTints,
      staffTints: lightStaffTints,
    },
    shadows: lightShadows,
  },
  [THEMES.DARK]: {
    components: {
      MuiTableHead: {
        root: {
          borderRadius: 5,
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottom: "1px solid rgba(145, 158, 171, 0.24)",
          },
        },
      },
    },
    palette: {
      topNavSvg: 'white',
      iosMoveSVG: 'white',
      basicBlackWhite: 'white',
      basicWhiteBlack: 'black',
      chartColors: [
        '#E81818',
        '#E28000',
        '#4ED040',
        '#40D0BC',
        '#6456E2',
        '#D040A5',
        '#0A74FF',
        '#9DA200',
        '#D28080',
        '#B87819',
        '#00B2DF',
        '#9933FF',
        '#EC699A',
        '#E3BE00',
        '#F29B17'
      ],
      dashboardWarning: {
        high: {
          main: '#D22626',
          contrastText: "#ffffff",
          svgColor: "#ee404c"
        },
        medium: {
          main: '#e3750a',
          contrastText: "#ffffff",
          svgColor: "#ffa30b"
        },
        low: {
          main: '#e3c500',
          contrastText: "#ffffff",
          svgColor: "#ffee00"
        },
        info: {
          main: '#569965',
          contrastText: '#ffffff',
          svgColor: '#ffffff'
        }
      },
      reportTabs: '#919eab',
      printerLocationButton: {
        unselected: {
          border: '#919eab',
          text: '#919eab',
          hoverBackground: 'rgba(255,255,255,0.05)',
        },
      },
      segmentedControl: {
        container: 'rgba(255, 255, 255, 0.05)',
        activeBackground: 'rgba(255, 255, 255, 0.1)',
        activeText: '#ffffff',
        inactiveText: '#919eab',
        hoverBackground: 'rgba(255, 255, 255, 0.08)',
      },
      pricingLevels: {
        inputBorder: '#9463A4',
        inputBackground: 'rgba(255, 255, 255, 0.05)',
        inputUnselectedBackground: 'rgba(255, 255, 255, 0.03)',
        cardShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
        cardHoverShadow: '0 4px 16px rgba(0, 0, 0, 0.4)',
        cardDisabledShadow: '0 1px 4px rgba(0, 0, 0, 0.2)',
        chipBackground: 'rgba(148, 99, 164, 0.3)',
        searchBarBackground: 'rgba(255, 255, 255, 0.08)',
      },
      card: {
        background: '#222b36'
      },
      chart: {
        even: "#723087",
        odd: "#8b6ba3",
      },
      white: "#ffffff",
      darkGrey: "#919eab",
      border: "rgba(145, 158, 171, 0.24)",
      disabled: "#4a5568",
      overlay: "rgba(0, 0, 0, 0.70)",
      editButtonBackground: "rgba(255, 255, 255, 0.2)",
      imageEditButtonBackground: "rgba(0, 0, 0, 0.7)",
      imageEditButtonHoverBackground: "rgba(0, 0, 0, 0.85)",
      boxShadow: "#919eab",
      box: "#b975e0",
      secondaryTitle: "#919eab",
      svg: "#919eab",
      delete: "#f44336",
      autoMessages: {
        promoBoxBackground: 'rgba(235, 66, 66, 0.3)',
        promoCode: '#EB4242 '
      },
      sendType: {
        sms: '#E3C500',
        email: '#1CB1DE',
        notification: '#E3750A',
        whatsapp: '#83BD37'
      },
      selected: {
        chip: "#723087",
      },
      autocompleteChip: "rgba(255, 255, 255, 0.1)",
      previewBackground: "rgba(255, 255, 255, 0.05)",
      cardShadow: "rgba(0, 0, 0, 0.2)",
      buttonShadow: "rgba(0, 0, 0, 0.5)",
      skeleton: {
        light: "rgba(255, 255, 255, 0.1)",
        dark: "rgba(255, 255, 255, 0.15)",
      },
      descriptionItemBackground: "rgba(255, 255, 255, 0.05)",
      iconPreviewBackground: "rgba(223, 236, 208, 0.2)",
      iconOverlay: "rgba(255, 255, 255, 0.1)",
      iconButtonShadow: "rgba(255, 255, 255, 0.1)",
      unselectedTabBackground: "rgba(255, 255, 255, 0.05)",
      dateIconBackground: "rgba(152, 144, 155, 0.3)",
      editingOverlay: "rgba(255, 255, 255, 0.1)",
      scrollbarBackground: "rgba(255, 255, 255, 0.1)",
      scrollbarThumb: "rgba(255, 255, 255, 0.3)",
      phoneInput: {
        dropdownBoxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
        scrollbarTrack: 'rgba(255, 255, 255, 0.05)',
        scrollbarThumb: 'rgba(255, 255, 255, 0.2)',
        scrollbarThumbHover: 'rgba(255, 255, 255, 0.3)',
      },
      avatar: {
        fallbackBackground: "rgba(255, 255, 255, 0.1)",
      },
      shadow: {
        input: "0px 1px 2px rgba(0, 0, 0, 0.3), 0px 0px 0px 1px rgba(0, 0, 0, 0.1)",
      },
      cancelButton: {
        main: "#424242",
        hover: "#616161",
      },
      copyButton: {
        default: {
          background: "#4a5568",
          color: "#ffffff",
          hover: "#5a6578",
        },
        copied: {
          background: "#4caf50",
          color: "#ffffff",
          hover: "#43a047",
        },
      },
      switch: {
        thumb: {
          light: "#ffffff",
          dark: "#bdbdbd",
          color: "#bdbdbd",
        },
        track: {
          light: "#b7b7b7",
          dark: "#616161",
        },
        focusVisible: {
          light: "#33cf4d",
          dark: "#4caf50",
        },
        focusBorder: {
          light: "#ffffff",
          dark: "#222b36",
        },
      },
      orderStatus: {
        isPaid: "#4caf50",
        notPaid: "#f44336",
        canceledBackground: "rgba(255, 87, 0, 0.28)",
        delayedBackground: "rgba(255, 0, 0, 0.18)",
        rejectedBackground: "rgba(192, 192, 192, 0.5)",
      },
      orderType: {
        dineIn: { main: "#9463A4", selected: '#f4e1fe', transparent: 'rgba(169, 101, 230, 0.08)' },
        delivery: { main: '#4077D0', selected: 'rgba(169, 201, 252, 0.6)', transparent: '#4077D030' },
        takeOut: { main: '#f97316', selected: 'rgba(252, 198, 144, 0.5)', transparent: '#f973161A' },
      },
      userMenu: {
        background: "#222b36",
        icons: "#723087",
      },
      messages: {
        background: "#1e2530",
        headerBackground: "#2a3441",
      },
      pageTitle: "#ffffff",
      rateStar: "#ffc107",
      navItems: {
        active: {
          subText: "#723087",
          text: "#ffffff",
          arrow: "#ffffff",
          background: "rgba(114, 48, 135, 0.1)"
        },
        default: {
          text: "#919eab",
          background: "transparent",
          hover: "rgba(255, 255, 255, 0.05)"
        },
        arrow: "#919eab",
      },
      menuButton: {
        activeBackgroundColor: "#2a3441",
        backgroundColor: "#222b36",
      },
      disabledSearch: {
        background: "#2a3a4f",
      },
      titleBox: {
        contrastText: "#ffffff",
        backgroundColor: "#723087",
      },
      action: {
        active: "#919eab",
      },
      background: {
        default: "#171c24",
        paper: "#222b36",
        hover: "#2a3441",
        selected: "#1e2530",
        chipSelected: "#2a3441",
        tableDropdown: "#2a3441",
        tableEven: "#1e2530",
        tableOdd: "#222b36",
        shape: '#72308740',
      },
      activity: {
        isActive: "#723087",
        isInactive: "#4a5568",
        isAway: "#FFBD08",
      },
      error: {
        contrastText: "#ffffff",
        main: "#f44336",
        light: '#ffcccc',
      },
      secondary: {
        contrastText: "#ffffff",
        main: "#5b5b5b",
        light: '#9D9D9D'
      },
      whiteButton: {
        contrastText: "#ffffff",
        main: "#2a3441",
      },
      primaryContrast: {
        contrastText: "#ffffff",
        main: "#222b36",
      },
      mode: "dark",
      primary: {
        contrastText: "#ffffff",
        main: "#9463A4",
        light: '#381c42',
      },
      secondaryTable: {
        contrastText: "#ffffff",
        main: "#4a5568",
        mainLight: '#723087'
      },
      success: {
        contrastText: "#ffffff",
        main: "#4caf50",
        light: 'rgba(76, 175, 80, 0.15)',
        dark: '#2e7d32',
      },
      text: {
        primary: "#ffffff",
        secondary: "#919eab",
        disabled: "#B4B4B4",
        third: '#ffffff'
      },
      warning: {
        contrastText: "#ffffff",
        main: "#ff9800",
        light: 'rgba(255, 152, 0, 0.15)',
        dark: '#e65100',
      },
      sidebarSelectedBackground: '#2a3441',
      sidebar: {
        activeText: '#ffffff',
        inactiveText: '#919eab',
        secondaryText: '#b0b8c4',
        scrollbar: '#4a5568',
        divider: 'rgba(145, 158, 171, 0.2)',
        boxShadow: 'inset 0 0 5px rgba(0, 0, 0, 0.5)',
        dropdownShadow: '0px 4px 20px rgba(0, 0, 0, 0.3)',
        icon: 'white',
        logout: "#f44336",
        text: "#919eab",
        // Hovered rows (the rail's hover pill is the primary colour).
        hoverBg: 'rgba(255, 255, 255, 0.08)',
        shadow: '2px 0 8px 0 rgba(0, 0, 0, 0.40)',
      },
      dashboardTabs: {
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
        textColor: '#ffffff',
        hoverBackgroundColor: 'rgba(255, 255, 255, 0.08)',
      },
      dashboard: {
        linearGradient: {
          start: "#9463A4",
          end: "#222b36"
        }
      },
      link: {
        main: '#64b5f6',
        hover: '#90caf9',
      },
      salesCards: {
        revenue: {
          background: 'rgba(16, 185, 129, 0.15)',
          border: 'rgba(16, 185, 129, 0.3)',
          icon: '#10B981',
          text: '#34D399',
        },
        orders: {
          background: 'rgba(59, 130, 246, 0.15)',
          border: 'rgba(59, 130, 246, 0.3)',
          icon: '#3B82F6',
          text: '#60A5FA',
        },
        customers: {
          background: 'rgba(139, 92, 246, 0.15)',
          border: 'rgba(139, 92, 246, 0.3)',
          icon: '#8B5CF6',
          text: '#A78BFA',
        },
        avgOrder: {
          background: 'rgba(245, 158, 11, 0.15)',
          border: 'rgba(245, 158, 11, 0.3)',
          icon: '#F59E0B',
          text: '#FBBF24',
        },
        discount: {
          background: 'rgba(239, 68, 68, 0.15)',
          border: 'rgba(239, 68, 68, 0.3)',
          icon: '#EF4444',
          text: '#F87171',
        },
      },
      roleTints: darkRoleTints,
      staffTints: darkStaffTints,
    },
    shadows: darkShadows,
  },
  [THEMES.NATURE]: {
    components: {
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottom: "1px solid rgba(145, 158, 171, 0.24)",
          },
        },
      },
    },
    palette: {
      background: {
        default: "#1c2531",
        paper: "#293142",
      },
      divider: "rgba(145, 158, 171, 0.24)",
      error: {
        contrastText: "#ffffff",
        main: "#f44336",
        light: '#ffcccc',
      },
      mode: "dark",
      primary: {
        contrastText: "#ffffff",
        main: "#01ab56",
        light: "#7648A3",
      },
      success: {
        contrastText: "#ffffff",
        main: "#4caf50",
        light: 'rgba(76, 175, 80, 0.15)',
        dark: '#2e7d32',
      },
      text: {
        primary: "#ffffff",
        secondary: "#919eab",
      },
      warning: {
        contrastText: "#ffffff",
        main: "#ff9800",
        light: 'rgba(255, 152, 0, 0.15)',
        dark: '#e65100',
      },
      orderType: {
        dineIn: { main: "#9463A4", selected: '#f4e1fe', transparent: 'rgba(169, 101, 230, 0.08)' },
        delivery: { main: '#4077D0', selected: 'rgba(169, 201, 252, 0.6)', transparent: '#4077D030' },
        takeOut: { main: '#f97316', selected: 'rgba(252, 198, 144, 0.5)', transparent: '#f973161A' },
      },
      roleTints: darkRoleTints,
      staffTints: darkStaffTints,
    },
    shadows: darkShadows,
  },
};

/**
 * `stableFieldErrors` setting: a field's error text floats in the gap under the field instead of
 * pushing the form down, so fields don't move (and clicks don't land on the wrong one) as errors
 * come and go. Only error text inside a field (TextField, Autocomplete, Select) is affected; give a
 * FormHelperText the `helper-in-flow` class to keep it in the layout.
 */
const stableFieldErrorsOptions = {
  components: {
    MuiFormControl: {
      styleOverrides: {
        root: {
          "& > .MuiFormHelperText-root.Mui-error:not(.helper-in-flow)": {
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            marginTop: 2,
            lineHeight: 1.25,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            pointerEvents: "none",
            zIndex: 1,
          },
        },
      },
    },
  },
};

export const createCustomTheme = (config = {}) => {
  let themeOptions = themesOptions[config.theme];

  if (!themeOptions) {
    console.warn(new Error(`The theme ${config.theme} is not valid`));
    themeOptions = themesOptions[THEMES.LIGHT];
  }

  let theme = createTheme(
    merge(
      {},
      baseOptions,
      config.stableFieldErrors !== false ? stableFieldErrorsOptions : {},
      themeOptions,
      // {
      //   ...(config.roundedCorners && {
      //     shape: {
      //       borderRadius: 16,
      //     },
      //   }),
      // },
      {
        direction: config.direction,
      }
    )
  );

  if (config.responsiveFontSizes) {
    theme = responsiveFontSizes(theme);
  }

  return theme;
};
